import {
  Card,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Button,
  Collapse,
  Skeleton,
} from "@mui/material";
import { Box } from "@mui/system";
import { styled } from "@mui/material/styles";
import { linearProgressClasses } from "@mui/material/LinearProgress";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../assets/styles/tokens";
import PerformanceItemMoreMetrics from "./PerformanceItemMoreMetrics";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React from "react";
import {
  formatValue,
  getCurrencySymbol,
  calcDiffPercent,
  OutputMetaField,
} from "../../../../../helpers/bizDevelopmentDashboardHelpers";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: spacing.Spacing.md.$value,
  borderRadius: spacing.Radius.xxs.$value,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: colors.Brand[500].$value,
    ...theme.applyStyles("dark", { backgroundColor: colors.Brand[500].$value }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: spacing.Radius.xxs.$value,
    backgroundColor: colors.Brand[800].$value,
    ...theme.applyStyles("dark", { backgroundColor: colors.Brand[800].$value }),
  },
}));

interface PerformanceItemCardProps {
  website_name: string;
  website_id: string;
  totalIncome: string;
  cpa: string;
  cpaPercentage: string;
  rs: string;
  rsPercentage: string;
  reg: string;
  clicks: string;

  ftd: string;
  ftd_percentage: string;
  qftd: string;
  qftd_percentage: string;
  ftd_incent: string;
  ftd_incent_percentage: string;

  rawRow: Record<string, any>;

  isMtd: boolean;
  isComparisonActive: boolean;
  isComparisonLoading: boolean;
  comparisonRow?: Record<string, any>;
  outputMetadata: Record<string, OutputMetaField>;
}

const fmtCurrency = (key: string, value: any): string => {
  const symbol = getCurrencySymbol(key);
  const str = formatValue(value);
  if (str === "N/A") return str;
  return symbol ? `${symbol}${str}` : str;
};

const fmtPerc = (raw: any): string => {
  if (raw === null || raw === undefined) return "N/A";
  const n = parseFloat(String(raw));
  if (isNaN(n)) return "N/A";
  return `${(n * 100).toFixed(1)}%`;
};

const diffLabel = (primary: any, comp: any): string | null => {
  const d = calcDiffPercent(primary, comp);
  if (d === null) return null;
  return `${d >= 0 ? "+" : ""}${d.toFixed(1)}%`;
};

export function PerformanceItemCard({
  website_name,
  website_id,
  totalIncome,
  cpa,
  cpaPercentage,
  rs,
  rsPercentage,
  reg,
  clicks,
  ftd,
  ftd_percentage,
  qftd,
  qftd_percentage,
  ftd_incent,
  ftd_incent_percentage,
  rawRow,
  isMtd,
  isComparisonActive,
  isComparisonLoading,
  comparisonRow,
  outputMetadata,
}: PerformanceItemCardProps) {
  const [open, setOpen] = React.useState(false);

  // Card-level abbreviations: the metadata title for these is the full key name
  // (e.g. "revshare_income_usd" → "REVSHARE INCOME USD") which is too long for the chip row.
  const CARD_SHORT_LABELS: Record<string, string> = {
    cpa_income_usd: "CPA",
    revshare_income_usd: "RS",
    total_income_usd: "TOTAL INCOME",
    reg: "Regs",
    clicks: "Click out, of which:",
    ftd_incent: "QFTD INCENT",
    ftd_incent_perc: "INCENT & TIER 5",
  };

  // Original mobile badge colors use design tokens, not the desktop hex values from getBadgeStyle
  const getMobileFtdStyle = (
    percKey: string,
    percent: number | null,
  ): { bg: string; color: string } => {
    if (percent === null)
      return { bg: colors.GrayLightMode[100].$value, color: colors.GrayLightMode[700].$value };
    if (percKey === "qftd_perc" && isMtd) {
      if (percent >= 20)
        return { bg: colors.Error[50].$value, color: colors.Error[600].$value };
      if (percent > 10)
        return {
          bg: colors.Warning[50].$value,
          color: colors.Warning[600].$value,
        };
      return {
        bg: colors.Success[50].$value,
        color: colors.Success[600].$value,
      };
    }
    if (percKey === "ftd_incent" && isMtd) {
      if (percent >= 20)
        return { bg: colors.Error[50].$value, color: colors.Error[600].$value };
      if (percent > 10)
        return { bg: colors.Warning[50].$value, color: colors.Warning[600].$value };
      return { bg: colors.Success[50].$value, color: colors.Success[600].$value };
    }
    if (percKey === "ftd_incent_perc" && isMtd) {
      return percent >= 25
        ? { bg: colors.Error[50].$value, color: colors.Error[600].$value }
        : { bg: colors.Success[50].$value, color: colors.Success[600].$value };
    }
    if (percent <= 12)
      return { bg: colors.Error[50].$value, color: colors.Error[600].$value };
    if (percent < 20)
      return {
        bg: colors.Warning[50].$value,
        color: colors.Warning[600].$value,
      };
    return { bg: colors.Success[50].$value, color: colors.Success[600].$value };
  };

  const ftdPercNum = ftd_percentage === "N/A" ? null : parseFloat(ftd_percentage.replace("%", ""));
  const qftdPercNum = qftd_percentage === "N/A" ? null : parseFloat(qftd_percentage.replace("%", ""));
  const ftdIncentPercNum = ftd_incent_percentage === "N/A" ? null : parseFloat(ftd_incent_percentage.replace("%", ""));
  const ftdIncentValueNum = ftd_incent === "N/A" ? null : parseFloat(ftd_incent);

  const ftdBadge = getMobileFtdStyle("ftd_perc", ftdPercNum);
  const qftdBadge = getMobileFtdStyle("qftd_perc", qftdPercNum);
  const ftdIncentBadge = getMobileFtdStyle("ftd_incent_perc", ftdIncentPercNum);
  const ftdIncentValueBadge = getMobileFtdStyle("ftd_incent", ftdIncentValueNum);

  const cpaBrandBg = colors.Brand[800].$value;
  const rsBrandBg = colors.Brand[500].$value;

  const getLabel = (key: string): string => {
    if (CARD_SHORT_LABELS[key]) return CARD_SHORT_LABELS[key];
    const entry = outputMetadata[key];
    if (entry?.title) return entry.title.replace(/_/g, " ").toUpperCase();
    return key.replace(/_/g, " ").toUpperCase();
  };

  const hasCmpRow = isComparisonActive && comparisonRow !== undefined;
  // Comparison was requested, loaded, but this brand has no matching row
  const isMissingComp =
    isComparisonActive && !comparisonRow && !isComparisonLoading;

  const cmp_ftd = hasCmpRow ? formatValue(comparisonRow!.ftd) : null;
  const cmp_ftd_perc = hasCmpRow ? fmtPerc(comparisonRow!.ftd_perc) : null;

  const cmp_qftd = hasCmpRow ? formatValue(comparisonRow!.qftd) : null;
  const cmp_qftd_perc = hasCmpRow ? fmtPerc(comparisonRow!.qftd_perc) : null;

  const cpaPercProgress = parseFloat(cpaPercentage.replace("%", "")) || 0;

  return (
    <Card
      sx={{
        p: 1,
        borderRadius: spacing.Radius.lg.$value,
        boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
        border: "1px solid " + colors.GrayLightMode[200].$value,
        mb: spacing.Spacing.none.$value,
        mt: spacing.Spacing.xl.$value + "!important",
      }}
    >
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="body1"
          sx={{
            color: colors.GrayLightMode[700].$value,
            fontSize: typography.Size.Text.xs.$value,
            fontWeight: typography.Font_weight.Medium.$value,
            mb: spacing.Spacing.md.$value,
          }}
        >
          {website_name} ({website_id})
        </Typography>
      </Stack>

      {/* Total Income */}
      <Stack sx={{ mb: spacing.Spacing.xs.$value }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: typography.Size.Text.xs.$value,
            color: colors.GrayLightMode[900].$value,
          }}
        >
          {getLabel("total_income_usd")}

          <Box
            component="span"
            sx={{
              fontSize: typography.Size.Text.xl.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              ml: spacing.Spacing.xs.$value,
            }}
          >
            {totalIncome}{" "}
            {isComparisonActive && (
              <>
                {isComparisonLoading ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    sx={{ display: "inline-block", ml: 0.5 }}
                  />
                ) : hasCmpRow ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: typography.Size.Text.xs.$value,
                      color: colors.GrayLightMode[500].$value,
                      ml: "2px",
                    }}
                  >
                    (
                    {fmtCurrency(
                      "total_income_usd",
                      comparisonRow!.total_income_usd,
                    )}
                    )
                  </Typography>
                ) : (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: typography.Size.Text.xs.$value,
                      color: colors.GrayLightMode[400].$value,
                      ml: "2px",
                    }}
                  >
                    No Data
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Typography>
      </Stack>

      {/* CPA & RS labels + chips */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: spacing.Spacing.xxs.$value }}
      >
        <Box display="flex" alignItems="center">
          <Typography
            variant="body2"
            sx={{
              color: colors.GrayLightMode[900].$value,
              mr: spacing.Spacing.md.$value,
            }}
          >
            {getLabel("cpa_income_usd")}
          </Typography>
          <Chip
            label={cpaPercentage}
            size="small"
            sx={{
              bgcolor: cpaBrandBg,
              color: colors.Base.White.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
            }}
          />
        </Box>
        <Box display="flex" alignItems="center">
          <Typography
            variant="body2"
            sx={{ color: colors.GrayLightMode[900].$value, mr: 1 }}
          >
            {getLabel("revshare_income_usd")}
          </Typography>
          <Chip
            label={rsPercentage}
            size="small"
            sx={{
              bgcolor: rsBrandBg,
              color: colors.Base.White.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
            }}
          />
        </Box>
      </Stack>

      {/* CPA & RS primary values */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="body2"
          sx={{
            color: colors.GrayLightMode[900].$value,
            fontWeight: typography.Font_weight.Bold.$value,
          }}
        >
          {cpa}
        </Typography>
        <Box display="flex" alignItems="center">
          <Typography
            variant="body2"
            sx={{
              color: colors.GrayLightMode[900].$value,
              fontWeight: typography.Font_weight.Semibold.$value,
            }}
          >
            {rs}
          </Typography>
        </Box>
      </Stack>

      {/* Progress Bar */}
      <BorderLinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, cpaPercProgress))}
        sx={{ my: 0.5 }}
      />

      {/* CPA & RS secondary row — comparison only: diff%(colored left) | comp value(gray right) */}
      {isComparisonActive &&
        (isComparisonLoading || hasCmpRow || isMissingComp) && (
          <Stack direction="row" justifyContent="space-between">
            {/* CPA side */}
            {isComparisonLoading ? (
              <Skeleton variant="text" width={60} />
            ) : isMissingComp ? (
              <Typography
                variant="body2"
                sx={{
                  color: colors.GrayLightMode[400].$value,
                  fontSize: typography.Size.Text.xs.$value,
                }}
              >
                No Data
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: spacing.Spacing.xs.$value,
                }}
              >
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    color: colors.GrayLightMode[500].$value,
                  }}
                >
                  {fmtCurrency("cpa_income_usd", comparisonRow!.cpa_income_usd)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    fontWeight: typography.Font_weight.Medium.$value,
                    color:
                      (calcDiffPercent(
                        rawRow.cpa_income_usd,
                        comparisonRow!.cpa_income_usd,
                      ) ?? 0) >= 0
                        ? colors.Success[600].$value
                        : colors.Error[600].$value,
                  }}
                >
                  {diffLabel(
                    rawRow.cpa_income_usd,
                    comparisonRow!.cpa_income_usd,
                  ) ?? ""}
                </Typography>
              </Box>
            )}
            {/* RS side */}
            {isComparisonLoading ? (
              <Skeleton variant="text" width={60} />
            ) : isMissingComp ? (
              <Typography
                variant="body2"
                sx={{
                  color: colors.GrayLightMode[400].$value,
                  fontSize: typography.Size.Text.xs.$value,
                }}
              >
                No Data
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: spacing.Spacing.xs.$value,
                }}
              >
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    color: colors.GrayLightMode[500].$value,
                  }}
                >
                  {fmtCurrency(
                    "revshare_income_usd",
                    comparisonRow!.revshare_income_usd,
                  )}
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    fontWeight: typography.Font_weight.Medium.$value,
                    color:
                      (calcDiffPercent(
                        rawRow.revshare_income_usd,
                        comparisonRow!.revshare_income_usd,
                      ) ?? 0) >= 0
                        ? colors.Success[600].$value
                        : colors.Error[600].$value,
                  }}
                >
                  {diffLabel(
                    rawRow.revshare_income_usd,
                    comparisonRow!.revshare_income_usd,
                  ) ?? ""}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

      {/* Regs & Click Out primary row */}
      <Stack
        direction="row"
        alignItems="center"
        mt={spacing.Spacing.md.$value}
        mb={spacing.Spacing.xs.$value}
      >
        <Typography
          variant="body2"
          sx={{
            color: colors.GrayLightMode[900].$value,
            fontWeight: typography.Font_weight.Bold.$value,
            mr: spacing.Spacing.xs.$value,
          }}
        >
          {reg}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.GrayLightMode[900].$value,
            mr: spacing.Spacing.xs.$value,
          }}
        >
          {getLabel("reg")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.GrayLightMode[900].$value,
            fontWeight: typography.Font_weight.Bold.$value,
            mr: spacing.Spacing.xs.$value,
          }}
        >
          {clicks}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.GrayLightMode[900].$value }}
        >
          {getLabel("clicks")}
        </Typography>
      </Stack>

      {/* Regs & Click Out comparison secondary row */}
      {isComparisonActive &&
        (isComparisonLoading || hasCmpRow || isMissingComp) && (
          <Stack
            direction="row"
            alignItems="center"
            mb={spacing.Spacing.xs.$value}
          >
            {isComparisonLoading ? (
              <Skeleton variant="text" width={80} />
            ) : isMissingComp ? (
              <Typography
                variant="body2"
                sx={{
                  color: colors.GrayLightMode[400].$value,
                  fontSize: typography.Size.Text.xs.$value,
                }}
              >
                No Data
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: colors.GrayLightMode[500].$value,
                  fontSize: typography.Size.Text.xs.$value,
                }}
              >
                {formatValue(comparisonRow!.reg)} Regs{" "}
                {formatValue(comparisonRow!.clicks)} Click out
              </Typography>
            )}
          </Stack>
        )}

      {/* FTD & QFTD — both always visible */}
      <Stack
        direction="row"
        justifyContent="space-between"
        mt={spacing.Spacing.xs.$value}
        mb={spacing.Spacing.xs.$value}
      >
        <Box
          sx={{
            width: "50%",
            borderRight: "1px solid " + colors.GrayLightMode[200].$value,
            pr: spacing.Spacing.md.$value,
          }}
        >
          {isMtd ? (
            /* MTD: label + value + chip on one line so INCENT fits below */
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography
                sx={{ color: colors.GrayLightMode[700].$value, fontSize: typography.Size.Text.xs.$value }}
              >
                {getLabel("ftd")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: spacing.Spacing.xs.$value }}>
                <Typography
                  sx={{
                    color: colors.GrayLightMode[700].$value,
                    fontSize: typography.Size.Text.sm.$value,
                    fontWeight: typography.Font_weight.Bold.$value,
                  }}
                >
                  {ftd}
                </Typography>
                <Chip
                  label={ftd_percentage}
                  size="small"
                  sx={{
                    borderRadius: spacing.Radius.xs.$value,
                    fontSize: typography.Size.Text.xs.$value,
                    bgcolor: ftdBadge.bg,
                    color: ftdBadge.color,
                    fontWeight: typography.Font_weight.Semibold.$value,
                  }}
                />
              </Box>
            </Box>
          ) : (
            /* Non-MTD: label on top, value + chip below */
            <>
              <Typography
                sx={{ color: colors.GrayLightMode[700].$value, fontSize: typography.Size.Text.xs.$value }}
              >
                {getLabel("ftd")}
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "2px" }}
              >
                <Typography
                  sx={{
                    color: colors.GrayLightMode[700].$value,
                    fontSize: typography.Size.Text.sm.$value,
                    fontWeight: typography.Font_weight.Bold.$value,
                  }}
                >
                  {ftd}
                </Typography>
                <Chip
                  label={ftd_percentage}
                  size="small"
                  sx={{
                    borderRadius: spacing.Radius.xs.$value,
                    fontSize: typography.Size.Text.xs.$value,
                    bgcolor: ftdBadge.bg,
                    color: ftdBadge.color,
                    fontWeight: typography.Font_weight.Semibold.$value,
                  }}
                />
              </Box>
            </>
          )}
          {/* FTD comparison secondary row: diff%(colored left) | comp value(gray right) */}
          {isComparisonActive && isComparisonLoading && (
            <Skeleton
              variant="text"
              width={60}
              sx={{ mt: spacing.Spacing.xxs.$value }}
            />
          )}
          {hasCmpRow && !isComparisonLoading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: spacing.Spacing.xxs.$value,
              }}
            >
              <Typography
                sx={{
                  color: colors.GrayLightMode[500].$value,
                  fontSize: typography.Size.Text.sm.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                }}
              >
                {cmp_ftd}
              </Typography>
              <Chip
                label={cmp_ftd_perc}
                size="small"
                sx={{
                  borderRadius: spacing.Radius.xs.$value,
                  fontSize: typography.Size.Text.xs.$value,
                  bgcolor: colors.GrayLightMode[100].$value,
                  color: colors.GrayLightMode[500].$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                }}
              />
            </Box>
          )}
          {isMissingComp && (
            <Typography
              sx={{
                color: colors.GrayLightMode[400].$value,
                fontSize: typography.Size.Text.xs.$value,
                mt: spacing.Spacing.xxs.$value,
              }}
            >
              No Data
            </Typography>
          )}
          {/* INCENT & TIER 5 — MTD only, inside FTD column */}
          {isMtd && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: spacing.Spacing.xs.$value,
              }}
            >
              <Typography
                sx={{
                  color: colors.GrayLightMode[700].$value,
                  fontSize: typography.Size.Text.xs.$value,
                }}
              >
                {getLabel("ftd_incent_perc")}
              </Typography>
              <Chip
                label={ftd_incent_percentage}
                size="small"
                sx={{
                  borderRadius: spacing.Radius.xs.$value,
                  fontSize: typography.Size.Text.xs.$value,
                  bgcolor: ftdIncentBadge.bg,
                  color: ftdIncentBadge.color,
                  fontWeight: typography.Font_weight.Semibold.$value,
                }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ width: "50%", pl: spacing.Spacing.md.$value }}>
            {isMtd ? (
              /* MTD: label + value + chip on one line so QFTD INCENT fits below */
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography
                  sx={{ color: colors.GrayLightMode[700].$value, fontSize: typography.Size.Text.xs.$value }}
                >
                  {getLabel("qftd")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: spacing.Spacing.xs.$value }}>
                  <Typography
                    sx={{
                      color: colors.GrayLightMode[700].$value,
                      fontSize: typography.Size.Text.sm.$value,
                      fontWeight: typography.Font_weight.Bold.$value,
                    }}
                  >
                    {qftd}
                  </Typography>
                  <Chip
                    label={qftd_percentage}
                    size="small"
                    sx={{
                      borderRadius: spacing.Radius.xs.$value,
                      fontSize: typography.Size.Text.xs.$value,
                      bgcolor: qftdBadge.bg,
                      color: qftdBadge.color,
                      fontWeight: typography.Font_weight.Semibold.$value,
                    }}
                  />
                </Box>
              </Box>
            ) : (
              /* Non-MTD: label on top, value + chip below */
              <>
                <Typography
                  sx={{ color: colors.GrayLightMode[700].$value, fontSize: typography.Size.Text.xs.$value }}
                >
                  {getLabel("qftd")}
                </Typography>
                <Box
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "2px" }}
                >
                  <Typography
                    sx={{
                      color: colors.GrayLightMode[700].$value,
                      fontSize: typography.Size.Text.sm.$value,
                      fontWeight: typography.Font_weight.Bold.$value,
                    }}
                  >
                    {qftd}
                  </Typography>
                  <Chip
                    label={qftd_percentage}
                    size="small"
                    sx={{
                      borderRadius: spacing.Radius.xs.$value,
                      fontSize: typography.Size.Text.xs.$value,
                      bgcolor: qftdBadge.bg,
                      color: qftdBadge.color,
                      fontWeight: typography.Font_weight.Semibold.$value,
                    }}
                  />
                </Box>
              </>
            )}
            {/* QFTD comparison secondary row: diff%(colored left) | comp value(gray right) */}
            {isComparisonActive && isComparisonLoading && (
              <Skeleton
                variant="text"
                width={60}
                sx={{ mt: spacing.Spacing.xxs.$value }}
              />
            )}
            {hasCmpRow && !isComparisonLoading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: spacing.Spacing.xxs.$value,
                }}
              >
                <Typography
                  sx={{
                    color: colors.GrayLightMode[500].$value,
                    fontSize: typography.Size.Text.sm.$value,
                    fontWeight: typography.Font_weight.Semibold.$value,
                  }}
                >
                  {cmp_qftd}
                </Typography>
                <Chip
                  label={cmp_qftd_perc}
                  size="small"
                  sx={{
                    borderRadius: spacing.Radius.xs.$value,
                    fontSize: typography.Size.Text.xs.$value,
                    bgcolor: colors.GrayLightMode[100].$value,
                    color: colors.GrayLightMode[500].$value,
                    fontWeight: typography.Font_weight.Semibold.$value,
                  }}
                />
              </Box>
            )}
            {isMissingComp && (
              <Typography
                sx={{
                  color: colors.GrayLightMode[400].$value,
                  fontSize: typography.Size.Text.xs.$value,
                  mt: spacing.Spacing.xxs.$value,
                }}
              >
                No Data
              </Typography>
            )}
            {/* QFTD INCENT — MTD only, inside QFTD column */}
            {isMtd && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: spacing.Spacing.xs.$value,
                }}
              >
                <Typography
                  sx={{
                    color: colors.GrayLightMode[700].$value,
                    fontSize: typography.Size.Text.xs.$value,
                  }}
                >
                  {getLabel("ftd_incent")}
                </Typography>
                <Chip
                  label={ftd_incent === "N/A" ? "N/A" : `${ftd_incent}%`}
                  size="small"
                  sx={{
                    borderRadius: spacing.Radius.xs.$value,
                    fontSize: typography.Size.Text.xs.$value,
                    bgcolor: ftdIncentValueBadge.bg,
                    color: ftdIncentValueBadge.color,
                    fontWeight: typography.Font_weight.Semibold.$value,
                  }}
                />
              </Box>
            )}
          </Box>
      </Stack>

      {/* Show More Metrics */}
      <Stack>
        <Button
          size="small"
          onClick={() => setOpen(!open)}
          sx={{
            padding: "0",
            fontSize: typography.Size.Text.xs.$value,
            lineHeight: typography.Line_height.text.xs.$value,
            color: colors.Brand[800].$value,
            fontWeight: typography.Font_weight.Regular.$value,
            textTransform: "capitalize",
            letterSpacing: "0px",
          }}
          data-tracker="654"
        >
          {open ? "Show Less Metrics" : "Show More Metrics"}
          <ExpandMoreIcon
            sx={{
              color: colors.Brand[800].$value,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "0.2s",
            }}
          />
        </Button>
        <Collapse in={open}>
          <PerformanceItemMoreMetrics
            rawRow={rawRow}
            isComparisonActive={isComparisonActive}
            isComparisonLoading={isComparisonLoading}
            comparisonRow={comparisonRow}
            outputMetadata={outputMetadata}
          />
        </Collapse>
      </Stack>
    </Card>
  );
}

export default PerformanceItemCard;
